import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Card, Row, Col, Form, Button, Table, Modal, Badge, Alert } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaReceipt } from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';

const Expenses = () => {
  const { user } = useContext(AuthContext);
  const [expenses, setExpenses] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [formData, setFormData] = useState({ expenseTypeId: '', amount: '', description: '', date: new Date().toISOString().split('T')[0] });
  const [typeFormData, setTypeFormData] = useState({ name: '', description: '' });
  const [editId, setEditId] = useState(null);
  const [editTypeId, setEditTypeId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchExpenses();
    fetchExpenseTypes();
  }, []);

  const fetchExpenses = async () => {
    try {
      const res = await axios.get('/api/expenses.php');
      setExpenses(res.data);
    } catch (err) {
      setError('Failed to load expenses');
    }
  };

  const fetchExpenseTypes = async () => {
    try {
      const res = await axios.get('/api/expense-types.php');
      setExpenseTypes(res.data);
    } catch (err) {
      setError('Failed to load expense types');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, expenseDate: formData.date };
      if (editId) {
        await axios.put(`/api/expenses.php?id=${editId}`, { ...payload, id: editId });
      } else {
        await axios.post('/api/expenses.php', payload);
      }
      setShowModal(false);
      setFormData({ expenseTypeId: '', amount: '', description: '', date: new Date().toISOString().split('T')[0] });
      setEditId(null);
      fetchExpenses();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save expense');
    }
  };

  const handleTypeSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editTypeId) {
        await axios.put(`/api/expense-types.php?id=${editTypeId}`, { ...typeFormData, id: editTypeId });
      } else {
        await axios.post('/api/expense-types.php', typeFormData);
      }
      setShowTypeModal(false);
      setTypeFormData({ name: '', description: '' });
      setEditTypeId(null);
      fetchExpenseTypes();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save expense type');
    }
  };

  const handleDeleteType = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        await axios.delete(`/api/expense-types.php?id=${id}`);
        fetchExpenseTypes();
      } catch (err) {
        setError('Failed to delete expense type');
      }
    }
  };

  const handleEditType = (type) => {
    setEditTypeId(type.id);
    setTypeFormData({ name: type.name, description: type.description || '' });
    setShowTypeModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        await axios.delete(`/api/expenses.php?id=${id}`);
        fetchExpenses();
      } catch (err) {
        setError('Failed to delete expense');
      }
    }
  };

  const handleEdit = (expense) => {
    setEditId(expense.id);
    const expenseDate = expense.expenseDate || expense.date;
    setFormData({ expenseTypeId: expense.expenseTypeId, amount: expense.amount, description: expense.description, date: expenseDate ? expenseDate.split('T')[0] : new Date().toISOString().split('T')[0] });
    setShowModal(true);
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString('en-PK', { style: 'currency', currency: 'PKR' });
  };

  const totalExpenses = expenses.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Expenses Management</h2>
        <div>
          {(user?.role === 'admin' || user?.permissions?.addExpense) && (
            <Button variant="primary" onClick={() => { setShowModal(true); setEditId(null); setFormData({ expenseTypeId: '', amount: '', description: '', date: new Date().toISOString().split('T')[0] }); }}>
              <FaPlus className="me-2" /> Add Expense
            </Button>
          )}
        </div>
      </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      <Row className="mb-4">
        <Col md={6}>
          <Card className="border-0 shadow-sm" style={{borderLeft: '4px solid #dc3545'}}>
            <Card.Body>
              <p className="text-muted mb-1 small">TOTAL EXPENSES</p>
              <h3 className="mb-0 text-danger fw-bold">{formatCurrency(totalExpenses)}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="border-0 shadow-sm" style={{borderLeft: '4px solid #6c757d'}}>
            <Card.Body>
              <p className="text-muted mb-1 small">TOTAL ENTRIES</p>
              <h3 className="mb-0 text-secondary fw-bold">{expenses.length}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white py-3">
          <h5 className="mb-0 fw-bold">Expense Records</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead className="bg-light">
              <tr>
                <th className="border-0 py-3">Date</th>
                <th className="border-0 py-3">Type</th>
                <th className="border-0 py-3">Description</th>
                <th className="border-0 py-3 text-end">Amount</th>
                <th className="border-0 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((exp) => (
                <tr key={exp.id}>
                  <td className="py-3">{exp.expenseDate ? new Date(exp.expenseDate).toLocaleDateString('en-GB') : 'Invalid Date'}</td>
                  <td className="py-3"><Badge bg="info">{exp.expenseTypeName || 'N/A'}</Badge></td>
                  <td className="py-3">{exp.description}</td>
                  <td className="py-3 text-end fw-bold text-danger">{formatCurrency(exp.amount)}</td>
                  <td className="py-3 text-center">
                    {(user?.role === 'admin' || user?.permissions?.editBill) && (
                      <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handleEdit(exp)}><FaEdit /></Button>
                    )}
                    {(user?.role === 'admin' || user?.permissions?.deletePatient) && (
                      <Button variant="outline-danger" size="sm" onClick={() => handleDelete(exp.id)}><FaTrash /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editId ? 'Edit Expense' : 'Add Expense'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Expense Type</Form.Label>
              <Form.Select value={formData.expenseTypeId} onChange={(e) => setFormData({...formData, expenseTypeId: e.target.value})} required>
                <option value="">Select Type</option>
                {expenseTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Amount</Form.Label>
              <Form.Control type="number" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={3} value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Date</Form.Label>
              <Form.Control type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} required />
            </Form.Group>
            <Button variant="primary" type="submit" className="w-100">Save</Button>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showTypeModal} onHide={() => setShowTypeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editTypeId ? 'Edit Expense Type' : 'Add Expense Type'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleTypeSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Type Name</Form.Label>
              <Form.Control type="text" value={typeFormData.name} onChange={(e) => setTypeFormData({...typeFormData, name: e.target.value})} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={2} value={typeFormData.description} onChange={(e) => setTypeFormData({...typeFormData, description: e.target.value})} />
            </Form.Group>
            <Button variant="primary" type="submit" className="w-100">Save</Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Expenses;
